import * as React from "react";
import { History } from "lucide-react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import { Badge } from "@ando/ui";

import {
  PaginationControls,
  SearchInput,
  SortHeader,
  type SortDir,
  usePagination,
} from "./_shared";

/**
 * AuditLogList — sortable / searchable / paginated table of audit events
 * for a single org.
 *
 * v1 reference: ``ando-frontend/app/admin-v2/components/pages/
 * organizations/components/AuditLogList.tsx`` (stub w/ ComingSoonBadge).
 * The v1 stub depends on a BE endpoint
 * ``GET /platform-admin/organizations/{id}/audit-log`` that does NOT
 * exist yet (only ``GET /gdpr/audit-log`` is shipped and is user-scoped).
 *
 * v2 ports the full UX chrome (toolbar + sortable headers + pagination
 * + empty state) so the table lights up the second BE ships the
 * endpoint. Until then, callers pass ``rows={[]}`` and the table renders
 * the empty state. The parent (``OrgAuditTab``) owns the BE-gap banner.
 *
 * Pure presentational — accepts rows + handlers, owns local sort /
 * search / page state for ergonomics. ARIA: sortable headers expose
 * ``aria-sort``; empty state is announced via the surrounding ``role``.
 */

export interface AuditEventRow {
  id: string;
  occurred_at: string;
  actor_email: string | null;
  action: string;
  target: string;
  /** Optional inline diff / metadata — rendered as a Badge if present. */
  diff?: string | null;
}

export interface AuditLogListProps {
  rows: AuditEventRow[];
  /** Optional override for the empty state copy (e.g. "BE in arrivo"). */
  emptyHint?: React.ReactNode;
}

function compareEvents(
  a: AuditEventRow,
  b: AuditEventRow,
  col: string,
  dir: SortDir,
): number {
  if (dir === null) return 0;
  const sign = dir === "asc" ? 1 : -1;
  switch (col) {
    case "occurred_at":
      return (Date.parse(a.occurred_at) - Date.parse(b.occurred_at)) * sign;
    case "actor":
      return (a.actor_email ?? "").localeCompare(b.actor_email ?? "") * sign;
    case "action":
      return a.action.localeCompare(b.action) * sign;
    case "target":
      return a.target.localeCompare(b.target) * sign;
    default:
      return 0;
  }
}

export function AuditLogList({
  rows,
  emptyHint,
}: AuditLogListProps): React.ReactElement {
  const { i18n } = useLingui();
  const [search, setSearch] = React.useState("");
  const [sortCol, setSortCol] = React.useState<string | null>("occurred_at");
  const [sortDir, setSortDir] = React.useState<SortDir>("desc");
  const { page, setPage, pageSize, setPageSize } = usePagination(25);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (e) =>
        e.action.toLowerCase().includes(q) ||
        (e.actor_email ?? "").toLowerCase().includes(q) ||
        e.target.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const sorted = React.useMemo(() => {
    if (sortDir === null || sortCol === null) return filtered;
    return [...filtered].sort((a, b) => compareEvents(a, b, sortCol, sortDir));
  }, [filtered, sortCol, sortDir]);

  const paged = React.useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize],
  );

  const handleSort = (col: string, dir: SortDir): void => {
    setSortCol(dir === null ? null : col);
    setSortDir(dir);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          ariaLabel={i18n._(msg`Cerca evento di audit`)}
          placeholder={i18n._(msg`Cerca per azione, attore o target…`)}
        />
        <Badge variant="ghost" className="text-[11px] text-ink-3">
          {sorted.length}{" "}
          {sorted.length === 1 ? i18n._(msg`evento`) : i18n._(msg`eventi`)}
        </Badge>
      </div>
      <div className="overflow-x-auto rounded-lg border border-hairline bg-card/55">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="border-hairline border-b text-left">
              <th scope="col" className="px-3 py-2">
                <SortHeader
                  label={i18n._(msg`Quando`)}
                  columnId="occurred_at"
                  activeColumn={sortCol}
                  direction={sortDir}
                  onSort={handleSort}
                />
              </th>
              <th scope="col" className="px-3 py-2">
                <SortHeader
                  label={i18n._(msg`Attore`)}
                  columnId="actor"
                  activeColumn={sortCol}
                  direction={sortDir}
                  onSort={handleSort}
                />
              </th>
              <th scope="col" className="px-3 py-2">
                <SortHeader
                  label={i18n._(msg`Azione`)}
                  columnId="action"
                  activeColumn={sortCol}
                  direction={sortDir}
                  onSort={handleSort}
                />
              </th>
              <th scope="col" className="hidden px-3 py-2 md:table-cell">
                <SortHeader
                  label={i18n._(msg`Target`)}
                  columnId="target"
                  activeColumn={sortCol}
                  direction={sortDir}
                  onSort={handleSort}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-3 py-10 text-center text-[12px] text-ink-3"
                >
                  <History
                    className="mx-auto mb-2 h-5 w-5 opacity-40"
                    aria-hidden
                  />
                  <p className="font-medium text-ink-2">
                    {search
                      ? i18n._(msg`Nessun evento corrisponde alla ricerca`)
                      : i18n._(msg`Nessun evento da mostrare`)}
                  </p>
                  {emptyHint ? (
                    <p className="mx-auto mt-1 max-w-md text-[11.5px] text-ink-3">
                      {emptyHint}
                    </p>
                  ) : null}
                </td>
              </tr>
            ) : (
              paged.map((ev) => (
                <tr
                  key={ev.id}
                  className="border-hairline/60 border-b last:border-b-0 hover:bg-ando-orange-soft/15"
                >
                  <td className="h-8 px-3 py-2 align-middle text-ink-3 tabular-nums">
                    {new Date(ev.occurred_at).toLocaleString("it-IT")}
                  </td>
                  <td className="h-8 px-3 py-2 align-middle text-ink-1">
                    {ev.actor_email ?? (
                      <span className="text-ink-3">—</span>
                    )}
                  </td>
                  <td className="h-8 px-3 py-2 align-middle">
                    <Badge variant="outline" className="text-[10.5px]">
                      {ev.action}
                    </Badge>
                    {ev.diff ? (
                      <span className="ml-2 text-[11px] text-ink-3">
                        {ev.diff}
                      </span>
                    ) : null}
                  </td>
                  <td className="hidden h-8 px-3 py-2 align-middle text-ink-3 md:table-cell">
                    {ev.target}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <PaginationControls
        page={page}
        pageSize={pageSize}
        total={sorted.length}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}
